import { computed, inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { Analytics, getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { getPerformance } from 'firebase/performance';
import {
    fetchAndActivate,
    getRemoteConfig,
    getValue,
    RemoteConfig,
} from 'firebase/remote-config';
import { PlatformService } from './platform.service';
import { LoggingService } from './logging.service';

@Injectable({ providedIn: 'root' })
export class FirebaseService {
  private readonly env = inject(ENVIRONMENT);
  private readonly platformService = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);

  private app: FirebaseApp | null = null;

  private analytics: Analytics | null = null;

  private remoteConfig: RemoteConfig | null = null;

  private performance: ReturnType<typeof getPerformance> | null = null;

  readonly isInitialized = signal(false);

  readonly error = signal<string | null>(null);

  readonly maintenanceMode = signal(false);

  readonly maintenanceMessage = signal('The app is currently under maintenance. Please try again later.');

  readonly config = computed(() => ({
    maintenance_mode: this.maintenanceMode(),
    maintenance_message: this.maintenanceMessage(),
  }));

  private get isConfigured(): boolean {
    return !!this.env.appSettings.firebase?.apiKey;
  }

  async initialize(): Promise<void> {
    if (this.platformService.isNativeMobile()) {
      try {
        await FirebaseAnalytics.setEnabled({ enabled: true });
      } catch (error) {
        this.loggingService.logException(error, 'FirebaseService: Native analytics enable failed');
      }
      this.isInitialized.set(true);
      return;
    }

    if (!this.isConfigured) {
      this.loggingService.logWarning('FirebaseService: Firebase config not found in environment');
      this.isInitialized.set(true);
      return;
    }

    if (this.app) {
      return;
    }

    try {
      const firebaseConfig = this.env.appSettings.firebase;
      if (!firebaseConfig) {
        this.loggingService.logWarning('FirebaseService: Firebase config is undefined');
        this.isInitialized.set(true);
        return;
      }

      this.app = initializeApp(firebaseConfig);

      try {
        this.analytics = getAnalytics(this.app);
      } catch (error) {
        this.loggingService.logException(error, 'FirebaseService: Analytics initialization failed');
      }

      try {
        this.remoteConfig = getRemoteConfig(this.app);
        this.remoteConfig.settings.minimumFetchIntervalMillis = this.env.production ? 3600000 : 0;
        await fetchAndActivate(this.remoteConfig);

        this.loadMaintenanceConfig();
      } catch (error) {
        this.loggingService.logException(error, 'FirebaseService: Remote Config initialization failed');
      }

      try {
        this.performance = getPerformance(this.app);
      } catch (error) {
        this.loggingService.logException(error, 'FirebaseService: Performance initialization failed');
      }

      this.isInitialized.set(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize Firebase';
      this.loggingService.logException(err, 'FirebaseService: Initialization failed');
      this.error.set(errorMessage);
      this.isInitialized.set(true);
    }
  }

  logEvent(eventName: string, eventParams?: Record<string, unknown>): void {
    if (this.platformService.isNativeMobile()) {
      FirebaseAnalytics.logEvent({ name: eventName, params: eventParams }).catch((error: unknown) => {
        this.loggingService.logException(error, 'FirebaseService: Native logEvent failed');
      });
      return;
    }

    if (!this.analytics) {
      return;
    }

    try {
      logEvent(this.analytics, eventName, eventParams);
    } catch (error) {
      this.loggingService.logException(error, 'FirebaseService: Failed to log event');
    }
  }

  setUserId(userId: string | null): void {
    if (this.platformService.isNativeMobile()) {
      FirebaseAnalytics.setUserId({ userId }).catch((error: unknown) => {
        this.loggingService.logException(error, 'FirebaseService: Native setUserId failed');
      });
      return;
    }

    if (!this.analytics) {
      return;
    }

    try {
      setUserId(this.analytics, userId);
    } catch (error) {
      this.loggingService.logException(error, 'FirebaseService: Failed to set user ID');
    }
  }

  setUserProperties(properties: Record<string, string>): void {
    if (this.platformService.isNativeMobile()) {
      Promise.all(
        Object.entries(properties).map(([key, value]) =>
          FirebaseAnalytics.setUserProperty({ key, value }),
        ),
      ).catch((error: unknown) => {
        this.loggingService.logException(error, 'FirebaseService: Native setUserProperties failed');
      });
      return;
    }

    if (!this.analytics) {
      return;
    }

    try {
      setUserProperties(this.analytics, properties);
    } catch (error) {
      this.loggingService.logException(error, 'FirebaseService: Failed to set user properties');
    }
  }

  logPageView(pageName: string, pageLocation?: string): void {
    if (this.platformService.isNativeMobile()) {
      this.logEvent('page_view', {
        page_title: pageName,
      });
      return;
    }

    this.logEvent('page_view', {
      page_title: pageName,
      page_location: pageLocation || window.location.href,
      page_path: window.location.pathname,
    });
  }

  logScreenView(screenName: string, screenClass?: string): void {
    if (this.platformService.isNativeMobile()) {
      FirebaseAnalytics.setCurrentScreen({ screenName, screenClassOverride: screenClass ?? null }).catch((error: unknown) => {
        this.loggingService.logException(error, 'FirebaseService: Native setCurrentScreen failed');
      });
      return;
    }

    this.logEvent('screen_view', {
      firebase_screen: screenName,
      firebase_screen_class: screenClass,
    });
  }

  getRemoteConfigString(key: string, defaultValue = ''): string {
    if (!this.remoteConfig) {
      return defaultValue;
    }

    try {
      const value = getValue(this.remoteConfig, key);
      return value.asString() || defaultValue;
    } catch {
      return defaultValue;
    }
  }

  getRemoteConfigBoolean(key: string, defaultValue = false): boolean {
    if (!this.remoteConfig) {
      return defaultValue;
    }

    try {
      const value = getValue(this.remoteConfig, key);
      return value.asBoolean();
    } catch {
      return defaultValue;
    }
  }

  getRemoteConfigNumber(key: string, defaultValue = 0): number {
    if (!this.remoteConfig) {
      return defaultValue;
    }

    try {
      const value = getValue(this.remoteConfig, key);
      return value.asNumber();
    } catch {
      return defaultValue;
    }
  }

  getRemoteConfigJson<T>(key: string, defaultValue: T): T {
    if (!this.remoteConfig) {
      return defaultValue;
    }

    try {
      const value = getValue(this.remoteConfig, key);
      const jsonString = value.asString();
      return jsonString ? JSON.parse(jsonString) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  async refreshRemoteConfig(): Promise<void> {
    if (!this.remoteConfig) {
      return;
    }

    try {
      await fetchAndActivate(this.remoteConfig);
    } catch (error) {
      this.loggingService.logException(error, 'FirebaseService: Failed to refresh Remote Config');
    }
  }

  get isPerformanceEnabled(): boolean {
    return !!this.performance;
  }

  private readonly HOST_ENV_TO_CONFIG_KEY: Record<string, string> = {
    LOCAL: 'development',
    DV1: 'development',
    QA1: 'qa',
    SG1: 'uat',
    PROD: 'production',
  };

  getAppConfigValue(key: string, defaultValue = ''): string {
    try {
      const appConfigJson = this.getRemoteConfigString('AppConfig', '');
      if (!appConfigJson) return defaultValue;

      const appConfig = JSON.parse(appConfigJson);
      const hostEnv = this.env.appSettings?.hostEnv || 'LOCAL';
      const configKey = this.HOST_ENV_TO_CONFIG_KEY[hostEnv] || 'development';
      const envConfig = appConfig[configKey];

      if (envConfig && key in envConfig) {
        return String(envConfig[key]);
      }
      return defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private loadMaintenanceConfig(): void {
    try {
      const appConfigJson = this.getRemoteConfigString('AppConfig', '');

      if (!appConfigJson) {
        return;
      }

      const appConfig = JSON.parse(appConfigJson);
      const hostEnv = this.env.appSettings?.hostEnv || 'LOCAL';
      const configKey = this.HOST_ENV_TO_CONFIG_KEY[hostEnv] || 'development';

      const envConfig = appConfig[configKey];
      if (envConfig) {
        const isMaintenanceMode = envConfig.maintenance_mode ?? false;
        const maintenanceMsg = envConfig.maintenance_message_web || envConfig.maintenance_message || 'The website is currently under maintenance. Please try again later.';

        this.maintenanceMode.set(isMaintenanceMode);
        this.maintenanceMessage.set(maintenanceMsg);

      } else {
        this.loggingService.logWarning(`FirebaseService: No config found for environment: ${configKey}`);
      }
    } catch (err) {
      this.loggingService.logException(err, 'FirebaseService: Failed to parse AppConfig for maintenance mode');
    }
  }
}
